import { FC } from 'react';
import { ActionIcon, Flex, TextInput } from '@mantine/core';
import { IconDragDrop, IconDragDrop2, IconX } from '@tabler/icons';
import { useDrag, useDrop } from 'react-dnd-latest';
import { AnswerDto } from '../../Types/QuestionnaireT';
import { EventRichInput, RichInput } from '../tiptap/TiptapEditorInput';
import DocumentBubbleMenu from '../tiptap/toolbars/BubbleMenu';
import { QuestionBundleS } from '../../Service/QuestionBundleS';
import { TiptapS } from '../../Service/TiptapS';
import { useEventScope } from '../../Service/hooks/useEventScope';

interface Props {
  questionId: string;
  answer: AnswerDto;
  handleChange: (patch: Partial<AnswerDto>) => void;
  moveAnswer?: (answerId: string, targetAnswerId: string) => void;
  onDelete: () => void;
  isAdditional?: boolean;
}

const AnswerOptionItem: FC<Props> = ({
  questionId,
  answer,
  handleChange,
  onDelete,
  moveAnswer,
  isAdditional = false,
}) => {
  const { id: answerId, value } = answer;
  const { isEventScope } = useEventScope();

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'answer',
      canDrop: (item: AnswerDto) => item.id !== answerId,
      collect: (monitor: any) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== answerId,
        canDrop: !!monitor.canDrop(),
      }),
      drop: async (item: AnswerDto) => {
        if (moveAnswer) {
          moveAnswer(item.id, answerId);
        }
      },
    }),
    [answerId],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'answer',
      item: answer,
      collect: (monitor: any) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [answerId],
  );

  const deleteHandler = async () => {
    const deleted = await QuestionBundleS.deleteAnswer(answerId, questionId);
    if (deleted) {
      onDelete();
    }
  };

  return (
    <Flex ref={drop} gap="xs" align="center" sx={{ opacity: isDragging ? 0.5 : 1 }}>
      {!isAdditional && (
        <div ref={drag}>
          {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
          {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
        </div>
      )}
      <div style={{ flexGrow: 1 }}>
        {isAdditional && <TextInput disabled value={value} />}
        {!isAdditional && (
          <>
            {isEventScope && (
              <EventRichInput
                value={value ?? ''}
                patchField={async (htmlString) => {
                  const resJSON = await QuestionBundleS.editAnswer(answerId, { value: htmlString });
                  if (resJSON) {
                    handleChange({ value: htmlString });
                    return true;
                  }
                  return false;
                }}
                menu={<DocumentBubbleMenu />}
                styles={TiptapS.TextInputStyles}
              />
            )}
            {!isEventScope && (
              <RichInput
                value={value ?? ''}
                patchField={async (htmlString) => {
                  const resJSON = await QuestionBundleS.editAnswer(answerId, { value: htmlString });
                  if (resJSON) {
                    handleChange({ value: htmlString });
                    return true;
                  }
                  return false;
                }}
                menu={<DocumentBubbleMenu />}
                styles={TiptapS.TextInputStyles}
              />
            )}
          </>
        )}
      </div>
      <ActionIcon size="xs" onClick={deleteHandler}>
        <IconX />
      </ActionIcon>
    </Flex>
  );
};

export default AnswerOptionItem;
